import { useEffect } from 'react';

import classNames from 'classnames';
import { observer } from 'mobx-react';

import { getSlideOrder } from '../../components/FullPageDialogs/config';
import { ExternalPayments } from '../../features/externalPayments/ExternalPayments';
import { LobbyMainView } from '../../features/lobby';
import { useSetDailyBonusPopup } from '../../hooks/useDailyBonusPopup';
import { useForceDialogsBeforeFeatureManager } from '../../hooks/useForceDialogsBeforeFeatureManager';
import { useOpenSupportWithQs } from '../../hooks/useOpenSupportWithQs';
import { useSetPromoPopup } from '../../hooks/usePromoPopup';
import { usePurchaseDepositLimitsPreflight } from '../../hooks/usePurchaseDepositLimitsPreflight';
import type { CvvErrorListener } from '../../stores/payredeem/types';
import type { FullPageDialogs, Slide } from '../../stores/ui/types';
import { useStores } from '../../stores/useStores';

import styles from './Home.module.scss';

const getHomePageClassname = (prevSlide: Slide | null, openFullPageDialog: FullPageDialogs | null) => {
  const exitingHome = prevSlide === 'home' && openFullPageDialog != null;
  const enteringHome = openFullPageDialog == null && prevSlide != null;

  if (!exitingHome && !enteringHome) {
    return undefined;
  }

  const prevSlideOrder = getSlideOrder(prevSlide);
  const openDialogOrder = getSlideOrder(openFullPageDialog);

  if (prevSlide === 'home') {
    return openDialogOrder > prevSlideOrder ? styles.exitingToLeft : styles.exitingToRight;
  }

  return openDialogOrder > prevSlideOrder ? styles.enteringFromRight : styles.enteringFromLeft;
};

const Home = observer(() => {
  const { uiStore, payRedeemStore, portalStore, metagamesStore, appStore } = useStores();
  const { ensurePurchaseDepositAllowed } = usePurchaseDepositLimitsPreflight();

  const homePageClassName = getHomePageClassname(uiStore.prevSlide, uiStore.openFullPageDialog);

  useForceDialogsBeforeFeatureManager();
  useSetPromoPopup();
  useSetDailyBonusPopup();
  useOpenSupportWithQs();

  useEffect(() => {
    const cvvErrorListener: CvvErrorListener = (arg) => {
      void (async () => {
        const packageId = appStore.selectedPackage?.packageId ?? appStore.currentPaymentPackage?.packageId;
        const allowed = await ensurePurchaseDepositAllowed(packageId ?? null);
        if (!allowed) {
          return;
        }
        uiStore.setCloseDialog();
        portalStore.openPortal(() => (
          <ExternalPayments
            errorCode={arg.errorCode}
            errorDetails={arg.errorDetails}
            paymentMethod={arg.paymentMethod}
            presetIframeParams={arg.pendingTransactionParams}
            storeType="store"
          />
        ));
      })();
    };

    payRedeemStore.addCvvErrorListener(cvvErrorListener);

    return () => {
      payRedeemStore.removeCvvErrorListener(cvvErrorListener);
    };
  }, [
    appStore,
    appStore.selectedPackage?.packageId,
    appStore.currentPaymentPackage?.packageId,
    ensurePurchaseDepositAllowed,
    payRedeemStore,
    portalStore,
    uiStore,
  ]);

  return (
    <section
      className={classNames(homePageClassName, styles.homePage, {
        [styles.hidden]: metagamesStore.currentOpenMetagameEvent,
      })}
    >
      <LobbyMainView />
    </section>
  );
});

export default Home;
